import React, { useEffect, useState, useRef } from 'react';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import restaurantIcon from '../../assets/store.svg';
import driverIcon from '../../assets/car_top_down.png';
import customerIcon from '../../assets/home.svg';
import config from './config';

const getDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3;
  const phi1 = lat1 * Math.PI / 180;
  const phi2 = lat2 * Math.PI / 180;
  const deltaPhi = (lat2 - lat1) * Math.PI / 180;
  const deltaLambda = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
    Math.cos(phi1) * Math.cos(phi2) *
    Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const getBearing = (lat1, lon1, lat2, lon2) => {
  const toRad = deg => (deg * Math.PI) / 180;
  const toDeg = rad => (rad * 180) / Math.PI;
  const dLon = toRad(lon2 - lon1);
  const y = Math.sin(dLon) * Math.cos(toRad(lat2));
  const x =
    Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
    Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLon);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
};

// Synchronous rotation using a pre-loaded HTMLImageElement — no async/Promise needed
const rotateImageCached = (img, angle) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const targetSize = 60;
  canvas.width = targetSize;
  canvas.height = targetSize;
  ctx.translate(30, 30);
  ctx.rotate((angle * Math.PI) / 180);
  const scale = targetSize / Math.max(img.width, img.height);
  ctx.drawImage(img, (-img.width * scale) / 2, (-img.height * scale) / 2,
                img.width * scale, img.height * scale);
  return canvas.toDataURL();
};

// Circular mean — avoids the 359°/1° → 180° wrong-average trap
const circularMean = (angles) => {
  const sinSum = angles.reduce((s, a) => s + Math.sin(a * Math.PI / 180), 0);
  const cosSum = angles.reduce((s, a) => s + Math.cos(a * Math.PI / 180), 0);
  return (Math.atan2(sinSum / angles.length, cosSum / angles.length) * 180 / Math.PI + 360) % 360;
};

// Returns the shortest signed angular difference in (-180, 180]
const shortAngleDiff = (from, to) => {
  const d = ((to - from) % 360 + 360) % 360;
  return d > 180 ? d - 360 : d;
};

const TrackingMap = ({ order, driver }) => {
  const [icons, setIcons] = useState({ restaurant: '', customer: '' });
  const [driverIconUrl, setDriverIconUrl] = useState('');

  const prevDriver      = useRef(null);
  const carImageRef     = useRef(null);   // cached HTMLImageElement
  const currentBearing  = useRef(0);      // bearing currently on screen
  const targetBearing   = useRef(0);      // bearing we're animating toward
  const animFrameRef    = useRef(null);   // rAF handle
  const bearingWindow   = useRef([]);     // last 3 raw bearings for smoothing

  const containerStyle = { width: '100%', height: '100%', minHeight: '300px' };

  // Smooth rAF loop: lerps currentBearing toward targetBearing each frame
  const startAnimationLoop = () => {
    if (animFrameRef.current !== null) return; // already running; new target will be picked up

    const animate = () => {
      const diff = shortAngleDiff(currentBearing.current, targetBearing.current);

      if (Math.abs(diff) < 0.5) {
        currentBearing.current = targetBearing.current;
        animFrameRef.current = null;
        if (carImageRef.current)
          setDriverIconUrl(rotateImageCached(carImageRef.current, currentBearing.current));
        return;
      }

      // Ease-out: move 12% of remaining angular distance each frame (~300ms at 60fps)
      currentBearing.current = ((currentBearing.current + diff * 0.12) + 360) % 360;
      if (carImageRef.current)
        setDriverIconUrl(rotateImageCached(carImageRef.current, currentBearing.current));

      animFrameRef.current = requestAnimationFrame(animate);
    };

    animFrameRef.current = requestAnimationFrame(animate);
  };

  // Load static/restaurant/customer SVG icons
  useEffect(() => {
    const loadIcons = async () => {
      const restaurantIconUrl = await fetchSvgContent(restaurantIcon);
      const customerIconUrl = await fetchSvgContent(customerIcon);
      setIcons({ restaurant: restaurantIconUrl, customer: customerIconUrl });
    };
    loadIcons();
  }, []);

  // Preload car image once so all rotations are synchronous
  useEffect(() => {
    const img = new Image();
    img.src = driverIcon;
    img.onload = () => {
      carImageRef.current = img;
      setDriverIconUrl(rotateImageCached(img, 0));
    };
  }, []);

  // Cancel rAF on unmount
  useEffect(() => {
    return () => {
      if (animFrameRef.current !== null) cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  useEffect(() => {
    if (!driver) return;
    const currentLat = Number(driver.latitudeStr);
    const currentLng = Number(driver.longitudeStr);

    if (prevDriver.current) {
      const prevLat = Number(prevDriver.current.latitudeStr);
      const prevLng = Number(prevDriver.current.longitudeStr);

      if (getDistance(prevLat, prevLng, currentLat, currentLng) > 5) {
        // Add raw bearing to rolling window (max 3 entries)
        const raw = getBearing(prevLat, prevLng, currentLat, currentLng);
        const win = bearingWindow.current;
        win.push(raw);
        if (win.length > 3) win.shift();

        // Circular mean smooths out single noisy GPS pings
        const smoothed = circularMean(win);

        // Only rotate if heading actually changed meaningfully
        if (Math.abs(shortAngleDiff(currentBearing.current, smoothed)) >= 5) {
          targetBearing.current = smoothed;
          startAnimationLoop();
        }

        prevDriver.current = driver; // advance only on real movement
      }
      // sub-5m ping: GPS noise — keep prevDriver unchanged so next real move is computed correctly
    } else {
      prevDriver.current = driver;
      bearingWindow.current = [];
      // Icon rendered by the preload effect above
    }
  }, [driver]);

  const svgToDataUrl = (svg) => {
    const svgElement = new DOMParser().parseFromString(svg, 'image/svg+xml').documentElement;
    svgElement.setAttribute('width', '40');
    svgElement.setAttribute('height', '40');
    const svgString = new XMLSerializer().serializeToString(svgElement);
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svgString)}`;
  };

  const fetchSvgContent = async (url) => {
    const response = await fetch(url);
    const svgText = await response.text();
    return svgToDataUrl(svgText);
  };

  const getCenterLocation = (driver, order) => {
    if (driver != null) {
      return { lat: Number(driver.latitudeStr), lng: Number(driver.longitudeStr) };
    } else if (order) {
      if (order.customerAddress && order.customerAddress.latitudeStr != null) {
        return {
          lat: Number(order.customerAddress.latitudeStr),
          lng: Number(order.customerAddress.longitudeStr)
        };
      } else if (order.clientLocation && order.clientLocation.locationAddress) {
        return {
          lat: Number(order.clientLocation.locationAddress.latitudeStr),
          lng: Number(order.clientLocation.locationAddress.longitudeStr)
        };
      }
    }
    return { lat: 0, lng: 0 };
  };

  return (
    <div style={{ borderRadius: '24px', overflow: 'hidden', boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)', height: '100%' }}>
      <LoadScript googleMapsApiKey="AIzaSyBJqLOLs7S5jhQMJBGN4xA9TEVacFwvZm0">
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={getCenterLocation(driver, order)}
          zoom={config.selectedZoom}
          options={{
            gestureHandling: 'greedy',
            disableDefaultUI: true,
            zoomControl: true,
          }}
        >
          {icons.restaurant && order?.clientLocation?.locationAddress && (
            <Marker
              position={{
                lat: Number(order.clientLocation.locationAddress.latitudeStr),
                lng: Number(order.clientLocation.locationAddress.longitudeStr)
              }}
              icon={{ url: icons.restaurant, scaledSize: { width: 40, height: 40 } }}
            />
          )}
          {driverIconUrl && driver != null && (
            <Marker
              position={{
                lat: Number(driver.latitudeStr),
                lng: Number(driver.longitudeStr)
              }}
              icon={{
                url: driverIconUrl,
                scaledSize: { width: 60, height: 60 },
                anchor: { x: 30, y: 30 }
              }}
            />
          )}
          {icons.customer && order?.customerAddress && (
            <Marker
              position={{
                lat: Number(order.customerAddress.latitudeStr),
                lng: Number(order.customerAddress.longitudeStr)
              }}
              icon={{ url: icons.customer, scaledSize: { width: 40, height: 40 } }}
            />
          )}
        </GoogleMap>
      </LoadScript>
    </div>
  );
};

export default TrackingMap;
